"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight, Coins, Gift, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TierCard, type TierCta } from "./tier-card";
import { AiCreditPaywall } from "@/components/editor/ai-credit-paywall";
import { AiMonthlyCheckout } from "@/components/editor/settings/ai-monthly-checkout";
import { startAiMonthlyCheckout, refreshAiBilling } from "@/lib/actions/ai-billing-actions";
import { useAiCreditPaymentNotice } from "@/hooks/use-ai-credit-payment-notice";
import { AI_MONTHLY_PLANS, AI_MONTHLY_PLAN, formatCredits, getAiMonthlyPlan, type AiMonthlyPlan } from "@/lib/ai-credits/monthly-policy";
import { type AiCreditPricingConfig } from "@/lib/ai-credits/pricing";
import { previewAiPlanChange } from "@/lib/actions/ai-plan-change-actions";
import { AiPlanChangeDialog } from "@/components/editor/settings/ai-plan-change-dialog";
interface Props {
  access: "guest" | "reader" | "author";
  email?: string;
  enabled: boolean;
  pricing: AiCreditPricingConfig;
  account: {
    plan: {
      key: string;
    } | null;
    subscription: {
      status: string;
      cancelAtPeriodEnd?: boolean;
      currentPeriodEnd?: Date | null;
    } | null;
    pendingChange?: unknown;
  } | null;
}
export function CreditPricingCatalog({
  access,
  email = "",
  enabled,
  pricing,
  account
}: Props) {
  const router = useRouter();
  const confirmPayment = useAiCreditPaymentNotice(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [change, setChange] = useState<Awaited<ReturnType<typeof previewAiPlanChange>> | null>(null);
  const [checkout, setCheckout] = useState<{
    plan: AiMonthlyPlan;
    clientSecret: string;
  } | null>(null);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const subscribed = account?.subscription && !["none", "canceled", "incomplete_expired"].includes(account.subscription.status);
  const currentPlan = getAiMonthlyPlan(account?.plan?.key);
  const endsOn = subscribed && account?.subscription?.cancelAtPeriodEnd && account.subscription.currentPeriodEnd;
  const accessHref = access === "guest" ? "/login?redirect=%2Fcredit-pricing" : "/become-a-writer";
  async function subscribe(plan: AiMonthlyPlan, button: HTMLButtonElement) {
    trigger.current = button;
    setError(null);
    setPending(plan.key);
    try {
      if (subscribed) setChange(await previewAiPlanChange(plan.key));else setCheckout({
        plan,
        ...(await startAiMonthlyCheckout(plan.key))
      });
    } catch (error_0) {
      setError(error_0 instanceof Error ? error_0.message : "Couldn’t open checkout. Try again.");
    } finally {
      setPending(null);
    }
  }
  function planCta(plan_0: AiMonthlyPlan): TierCta {
    if (access !== "author") return {
      kind: "link",
      label: `Choose ${plan_0.name}`,
      href: accessHref
    };
    if (subscribed && account?.plan?.key === plan_0.key) return {
      kind: "current",
      label: "Current plan"
    };
    if (subscribed && (account?.pendingChange || account?.subscription?.status !== "active")) return {
      kind: "link",
      label: "Manage plan",
      href: "/editor/settings/billing"
    };
    return {
      kind: "action",
      label: pending === plan_0.key ? "Loading…" : `${subscribed ? currentPlan && plan_0.priceCents > currentPlan.priceCents ? "Upgrade to" : "Switch to" : "Choose"} ${plan_0.name}`,
      disabled: !!pending,
      onClick: event => void subscribe(plan_0, event.currentTarget)
    };
  }
  return <>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            {enabled && <div className="space-y-3">
                <h2 id="monthly-plans" className="text-xl font-semibold">Monthly plans</h2>
                <p className="flex items-center gap-2 text-sm text-muted-foreground"><Gift aria-hidden="true" className="size-4 text-primary" />Plus {formatCredits(AI_MONTHLY_PLAN.freeUnits)} free credits/month.</p>
            </div>}
            {access === "author" && <Link href="/editor/settings/billing" className="text-sm text-primary underline underline-offset-4">Billing</Link>}
        </div>
        {enabled && <section aria-labelledby="monthly-plans" className="space-y-6">
            {endsOn && <p className="text-sm text-muted-foreground">Your current plan ends {new Date(endsOn).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          timeZone: "UTC"
        })}.</p>}
            <div className="grid gap-4 md:grid-cols-3">
                {AI_MONTHLY_PLANS.map(plan_1 => <TierCard key={plan_1.key} name={plan_1.name} price={`$${plan_1.priceCents / 100}`} period="/month" tagline={`${formatCredits(plan_1.units)} credits/month`} variant={subscribed && account?.plan?.key === plan_1.key ? "primary" : "muted"} features={[]} appearance="simple" cta={planCta(plan_1)} />)}
            </div>
            <p className="flex items-center gap-2 text-sm text-muted-foreground"><Check aria-hidden="true" className="size-4 text-primary" />Cancel anytime. Paid credits never expire.</p>
        </section>}
        {error && <p role="alert" className="mt-6 text-destructive">{error}</p>}
        <section aria-labelledby="one-time-credits" className="mt-8 flex flex-col gap-6 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/10 to-card p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
            <div className="flex items-center gap-4">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Coins aria-hidden="true" className="size-5" /></span>
                <div><h2 id="one-time-credits" className="text-lg font-semibold">One-time credits</h2><p className="mt-1 text-sm text-muted-foreground">Choose your amount.</p></div>
            </div>
            {access === "author" ? <Button size="lg" className="h-12 rounded-xl px-6 text-base font-semibold" onClick={() => setTopUpOpen(true)}>Buy credits<ArrowRight aria-hidden="true" /></Button> : <Button size="lg" className="h-12 rounded-xl px-6 text-base font-semibold" asChild><Link href={accessHref}>Buy credits<ArrowRight aria-hidden="true" /></Link></Button>}
        </section>
        <p className="mt-6 text-sm text-muted-foreground">
            {enabled ? "Free credits expire monthly." : "Paid credits never expire."}
        </p>
        {change && <AiPlanChangeDialog preview={change} returnFocusRef={trigger} onClose={() => {
      setChange(null);
      router.refresh();
    }} onComplete={async () => {
      await refreshAiBilling();
      setChange(null);
      confirmPayment("monthly");
      router.refresh();
    }} />}
        {checkout && <AiMonthlyCheckout {...checkout} email={email} returnFocusRef={trigger} onClose={() => setCheckout(null)} onComplete={async () => {
      await refreshAiBilling();
      setCheckout(null);
      confirmPayment("monthly");
      router.refresh();
    }} />}
        <AiCreditPaywall initialCredits={pricing.suggested[0]} pricing={pricing} shortfall={null} open={topUpOpen} onOpenChange={setTopUpOpen} returnPath="/credit-pricing" />
    </>;
}